import { useState } from "react";
import { Share2, Copy, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useStepUp } from "./StepUpGuard";

/* Expiring Document Shares — share one sensitive document for a limited time. */
const HOURS = [["1", "1 hour"], ["24", "24 hours"], ["72", "3 days"], ["168", "7 days"]];

export function ShareDocButton({ docType, refIds, label }) {
  const guard = useStepUp();
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState("24");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/doc-shares", { doc_type: docType, expires_hours: Number(hours), ...refIds });
      const url = `${window.location.origin}${r.data.share_url}`;
      setLink(url);
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied — auto-revokes when it expires"); }
      catch { toast.success("Share link created"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create share"); }
    setBusy(false);
  };

  return (
    <span className="relative inline-block">
      <button onClick={() => guard(() => setOpen((o) => !o))} data-testid={`docshare-btn-${refIds.file_id || refIds.pass_id}`}
        title={`Share "${label}" with a time-limited link`}
        className="inline-flex items-center gap-1 text-[11px] font-600 border border-zinc-300 px-1.5 py-0.5 hover:border-[#4285F4] hover:text-[#4285F4]">
        <Share2 className="w-3 h-3" /> Share
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-zinc-300 shadow-lg p-3 w-64" data-testid="docshare-popover">
          <div className="text-xs font-700 mb-1.5 flex items-center gap-1"><Clock className="w-3 h-3" /> Time-limited share — {label}</div>
          {!link ? (
            <>
              <select value={hours} onChange={(e) => setHours(e.target.value)} data-testid="docshare-hours"
                className="w-full border border-zinc-300 px-2 py-1.5 text-xs mb-2 bg-white">
                {HOURS.map(([v, l]) => <option key={v} value={v}>Expires in {l}</option>)}
              </select>
              <p className="text-[10px] text-muted-foreground mb-2">Anyone with the link can view it until it expires or you revoke it (Privacy &amp; Security → shares).</p>
              <div className="flex gap-1.5">
                <button onClick={create} disabled={busy} data-testid="docshare-create"
                  className="flex-1 py-1.5 text-white text-xs font-700 disabled:opacity-50" style={{ background: "#4285F4" }}>
                  {busy ? "Creating…" : "Create link"}
                </button>
                <button onClick={() => setOpen(false)} className="px-2 py-1.5 text-xs border border-zinc-300">Close</button>
              </div>
            </>
          ) : (
            <>
              <div className="text-[10px] font-mono break-all border border-zinc-200 bg-zinc-50 p-1.5 mb-2" data-testid="docshare-link">{link}</div>
              <div className="flex gap-1.5">
                <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} data-testid="docshare-copy"
                  className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 text-white text-xs font-700" style={{ background: "#34A853" }}>
                  <Copy className="w-3 h-3" /> Copy link
                </button>
                <button onClick={() => { setOpen(false); setLink(""); }} className="px-2 py-1.5 text-xs border border-zinc-300">Done</button>
              </div>
            </>
          )}
        </div>
      )}
    </span>
  );
}
